import { useTranslation } from 'react-i18next';
import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDebounceValue } from 'usehooks-ts';
import * as S from './DesktopFilters.styles';
import { Input } from '@/components/UI/Input/Input';
import {
  PRICE_INPUT_MAX_LENGTH,
  PRICE_INPUT_PRECISION,
} from '@/components/Filters/FiltersDrawer.constants';
import { updateURLParameters } from '@/components/Filters/FiltersDrawer.helpers';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { formatRangeValue } from '@/components/Filters/DesktopFilters/DesktopFilters.helpers';
import { DesktopFilterAccordion } from '@/components/Filters/DesktopFilters/DesktopFilterAccordion';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { QUERY } from '@/constants/common';

interface Props {
  currentPrices: {
    from: string | null;
    to: string | null;
  };
  isLoading?: boolean;
}

export const PriceDesktopFilter: FC<Props> = ({ currentPrices, isLoading }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { currentCategory, isAllCategories } = useCurrentCategory();

  const [prices, setPrices] = useState<{
    from: string | null;
    to: string | null;
  }>(currentPrices);

  const [debouncedPrices] = useDebounceValue(prices, 1_000);

  useEffect(() => {
    applyNewPrices(debouncedPrices);
  }, [debouncedPrices]);

  useEffect(() => {
    if (!isFocusedOnInput()) {
      setPrices(currentPrices);
    }
  }, [currentPrices]);

  function isFocusedOnInput() {
    if (typeof window === 'undefined') return false;

    const activeId = document.activeElement?.id;
    return activeId && ['price-from', 'price-to'].includes(activeId);
  }

  function applyNewPrices(newPrices: typeof prices) {
    if (
      newPrices.from === currentPrices.from &&
      newPrices.to === currentPrices.to
    ) {
      return;
    }
    const newURL = updateURLParameters({
      url: window.location.href,
      params: {
        [`${QUERY.q}[${QUERY.price}][]`]: [
          newPrices.from ?? '',
          newPrices.to ?? '',
        ],
        [QUERY.keywordsSource]: KeywordsSource.Typewritten,
      },
      hasCategory: currentCategory && !isAllCategories,
    });

    router.push(newURL, undefined, { scroll: false });
  }

  function handleChange(value: string | null, fieldName: keyof typeof prices) {
    setPrices({ ...prices, [fieldName]: value });
  }

  function handleClear() {
    setPrices({ from: null, to: null });
    applyNewPrices({ from: null, to: null });
  }

  if (isLoading) {
    return (
      <S.DesktopFilterLoaderContainer>
        <S.DesktopFilterLoaderRow>
          <SkeletonLoader width={80} height={20} borderRadius={7} />
          <SkeletonLoader width={20} height={20} borderRadius={7} />
        </S.DesktopFilterLoaderRow>
        <S.DesktopFilterLoaderRow>
          <SkeletonLoader width={127.5} height={44} borderRadius={14} />
          <SkeletonLoader width={127.5} height={44} borderRadius={14} />
        </S.DesktopFilterLoaderRow>
      </S.DesktopFilterLoaderContainer>
    );
  }

  return (
    <DesktopFilterAccordion
      title={t('common.price_azn')}
      defaultOpen
      value={formatRangeValue(currentPrices, '₼', t)}
      onClear={handleClear}
      dataTestId={SearchResultsPageTestIds.desktopFilters.price}
    >
      <S.RangeFieldsContainer>
        <Input
          id="price-from"
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={prices.from?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'from')}
          type="search"
          appearance="filled"
          inputMode="decimal"
          placeholder={t('common.from')}
          label={t('common.from')}
          labelPosition="left"
          precision={PRICE_INPUT_PRECISION}
          customPaddingTop={2}
          className="string required form-control min"
          data-testid="price-from"
          maxLength={PRICE_INPUT_MAX_LENGTH}
        />
        <Input
          id="price-to"
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={prices.to?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'to')}
          type="search"
          appearance="filled"
          inputMode="decimal"
          placeholder={t('common.to')}
          label={t('common.to')}
          labelPosition="left"
          precision={PRICE_INPUT_PRECISION}
          customPaddingTop={2}
          className="string required form-control max"
          data-testid="price-to"
          maxLength={PRICE_INPUT_MAX_LENGTH}
        />
      </S.RangeFieldsContainer>
    </DesktopFilterAccordion>
  );
};
